import React, { useMemo } from 'react'
import { AppTable, FlexBox } from 'components'
import { Card } from 'semantic-ui-react'
import styled from 'styled-components'
import {
    formatRunWindow,
    formatUtcInTimezone,
    getLastRunBadge,
    getRunTrigger,
    getTriggerBadge,
    getViewerTimezone,
} from './consolidated-report-email-utils'
import { StatusBadge } from './consolidated-report-email-styles'

const ScheduleLink = styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    color: #2563eb;
    font-weight: 600;
    font-size: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;

    &:hover {
        color: #1d4ed8;
    }
`

const RunCard = ({ item, onRowClick, onScheduleClick, timezone }) => {
    if (!item) return null
    const statusBadge = getLastRunBadge(item.Status)
    const triggerBadge = getTriggerBadge(getRunTrigger(item))
    const canOpenSchedule = Boolean(onScheduleClick && item.ScheduleId)
    const viewerTimezone = getViewerTimezone(timezone)

    return (
        <Card fluid>
            <Card.Content>
                <Card.Header>
                    {canOpenSchedule ? (
                        <ScheduleLink
                            type="button"
                            title="Open schedule"
                            onClick={e => {
                                e.stopPropagation()
                                onScheduleClick(item)
                            }}
                        >
                            {item.ScheduleName || '—'}
                        </ScheduleLink>
                    ) : (
                        item.ScheduleName || '—'
                    )}
                </Card.Header>
                <Card.Meta>
                    {formatUtcInTimezone(item.FiredAtUtc, viewerTimezone)}
                </Card.Meta>
                <Card.Description>
                    <div>Window: {formatRunWindow(item, timezone)}</div>
                    <div>Reports: {item.ReportsIncluded ?? '—'}</div>
                    <div style={{ marginTop: 6 }}>
                        <StatusBadge $color={triggerBadge.color}>
                            {triggerBadge.label}
                        </StatusBadge>{' '}
                        <StatusBadge $color={statusBadge.color}>
                            {statusBadge.label}
                        </StatusBadge>
                    </div>
                    <FlexBox
                        className="justify-content-end mt-2"
                        style={{ gap: 8 }}
                    >
                        {canOpenSchedule && (
                            <button
                                type="button"
                                className="ui button mini basic"
                                onClick={e => {
                                    e.stopPropagation()
                                    onScheduleClick(item)
                                }}
                            >
                                Open schedule
                            </button>
                        )}
                        {onRowClick && (
                            <button
                                type="button"
                                className="ui button mini basic"
                                onClick={e => {
                                    e.stopPropagation()
                                    onRowClick(item)
                                }}
                            >
                                View
                            </button>
                        )}
                    </FlexBox>
                </Card.Description>
            </Card.Content>
        </Card>
    )
}

export const RunHistoryList = ({
    data,
    filter,
    pageChanged,
    totalCount,
    timezone,
    onRowClick,
    onScheduleClick,
    compact = false,
}) => {
    const columns = useMemo(() => {
        const viewerTimezone = getViewerTimezone(timezone)
        const cols = [
            {
                title: 'Schedule',
                render: item =>
                    onScheduleClick && item.ScheduleId ? (
                        <ScheduleLink
                            type="button"
                            title="Open schedule to edit or delete"
                            onClick={() => onScheduleClick(item)}
                        >
                            {item.ScheduleName || '—'}
                        </ScheduleLink>
                    ) : (
                        <strong>{item.ScheduleName || '—'}</strong>
                    ),
            },
            {
                title: 'Fired at',
                render: item => (
                    <span>
                        {formatUtcInTimezone(item.FiredAtUtc, viewerTimezone)}
                    </span>
                ),
            },
            {
                title: 'Window',
                render: item => <span>{formatRunWindow(item, timezone)}</span>,
            },
            {
                title: 'Reports',
                render: item => <span>{item.ReportsIncluded ?? '—'}</span>,
            },
            {
                title: 'Recipients',
                render: item => <span>{item.RecipientCount ?? '—'}</span>,
            },
            {
                title: 'Trigger',
                render: item => {
                    const badge = getTriggerBadge(getRunTrigger(item))
                    return (
                        <StatusBadge $color={badge.color}>
                            {badge.label}
                        </StatusBadge>
                    )
                },
            },
            {
                title: 'Status',
                render: item => {
                    const badge = getLastRunBadge(item.Status)
                    return (
                        <StatusBadge $color={badge.color}>
                            {badge.label}
                        </StatusBadge>
                    )
                },
            },
            ...(onRowClick
                ? [
                      {
                          title: 'Actions',
                          width: 80,
                          render: item => (
                              <button
                                  type="button"
                                  className="ui button mini basic"
                                  onClick={() => onRowClick(item)}
                              >
                                  View
                              </button>
                          ),
                      },
                  ]
                : []),
        ]
        return compact ? cols.filter(c => c.title !== 'Schedule') : cols
    }, [timezone, compact, onRowClick, onScheduleClick])

    return (
        <AppTable
            data={data || []}
            columns={columns}
            pageChanged={pageChanged}
            filter={filter}
            totalCount={totalCount}
            mobileCard={
                <RunCard
                    onRowClick={onRowClick}
                    onScheduleClick={onScheduleClick}
                    timezone={timezone}
                />
            }
        />
    )
}
